import React, {useEffect} from "react";
import {InputText} from 'primereact/inputtext';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {useAppDispatch, useAppSelector} from "redux/hooks";
import {InputNumber} from "primereact/inputnumber";
import {setCallCycles, setChangedCompaniesData, setCompanyField, setErrorFields} from "../../../redux/actions/actions";
import {classNames} from "primereact/utils";
import {InputSwitch} from "primereact/inputswitch";
import {Divider} from "primereact/divider";
import {Checkbox} from "primereact/checkbox";
import {RadioButton} from "primereact/radiobutton";
import {CALLS_DIALER_TYPE, resolveCallsDialerType} from "../../../types/types";

const QueueACDSetting = () => {
    const dispatch = useAppDispatch()
    const archiveTableToggle = useAppSelector(state => state.CompaniesListReducer.archiveTableToggle);
    const campaignDirectoriesPhoneType = useAppSelector(state => state.CompaniesListReducer.campaignDirectoriesPhoneType);
    const callCycles = useAppSelector(state => state.CompaniesListReducer.companiesData.callCycles);
    const errorFields = useAppSelector(state => state.CompaniesListReducer.errorFields);
    const companiesData = useAppSelector(state => state.CompaniesListReducer.companiesData);
    const statusDone = companiesData?.campaign?.statusId === 6;
    const editType = useAppSelector(state => state.UsersReducer.editType);


    useEffect(() => {
        if (campaignDirectoriesPhoneType?.length) {
            const activePhoneTypes = campaignDirectoriesPhoneType.filter(
                (item: { isActive?: boolean }) => item.isActive !== false
            );

            const existingTypeCalls = callCycles?.map((cycle: any) => cycle.typeCall) || [];
            const existingTotalCount = callCycles?.[0]?.totalCount ?? 1;
            const missingTypeCalls = activePhoneTypes
                .filter((item: { dictionaryId: any; }) => !existingTypeCalls.includes(item.dictionaryId))
                .map((item: { dictionaryId: any; orderBy: any; }) => ({
                    typeCall: item.dictionaryId,
                    countCalls: 1,
                    timeBetweenCalls: 0,
                    isActive: true,
                    priorityCallType: item.orderBy,
                    isFullCycle: false,
                    totalCount: existingTotalCount
                }));

            if (missingTypeCalls.length > 0) {
                const updatedCycles = [...(callCycles || []), ...missingTypeCalls];
                dispatch(setCallCycles(updatedCycles));
            } else if (!callCycles?.length) {
                const initialCycles = activePhoneTypes
                    .map((item: { dictionaryId: any; orderBy: any; }) => ({
                        typeCall: item.dictionaryId,
                        countCalls: 1,
                        timeBetweenCalls: 0,
                        isActive: true,
                        priorityCallType: item.orderBy,
                        isFullCycle: companiesData?.campaign?.isFullCycle ?? false,
                        totalCount: 1
                    }));

                dispatch(setCallCycles(initialCycles));
            }
        }
    }, [campaignDirectoriesPhoneType, callCycles]);

    useEffect(() => {
        if (Array.isArray(callCycles) && callCycles.length) {
            const shouldPatch = callCycles.some((cycle: any) => cycle.isActive === undefined);
            if (shouldPatch) {
                const patched = callCycles.map((cycle: any) => ({
                    ...cycle,
                    isActive: cycle.isActive === undefined ? true : cycle.isActive
                }));
                dispatch(setCallCycles(patched));
            }
        }
    }, [callCycles]);

    useEffect(() => {
        if (!Array.isArray(callCycles) || !callCycles.length) {
            return;
        }

        const hasPerRowFullCycle = callCycles.some((cycle: any) => cycle.isFullCycle !== undefined && cycle.isFullCycle !== null);
        if (!hasPerRowFullCycle && companiesData?.campaign?.isFullCycle !== undefined) {
            const withFullCycle = callCycles.map((cycle: any) => ({
                ...cycle,
                isFullCycle: companiesData.campaign.isFullCycle ?? false
            }));
            dispatch(setCallCycles(withFullCycle));
        }
    }, [callCycles, companiesData?.campaign?.isFullCycle]);

    useEffect(() => {
        if (resolveCallsDialerType(companiesData?.campaign) !== CALLS_DIALER_TYPE.cyclically || !Array.isArray(callCycles) || !callCycles.length) {
            return;
        }

        const baseCount = callCycles[0]?.countCalls ?? null;
        const baseTimeRaw = callCycles[0]?.timeBetweenCalls ?? null;

        const needsNormalization = callCycles.some((cycle: any) =>
            cycle.countCalls !== baseCount || cycle.timeBetweenCalls !== (baseCount === 1 ? 0 : baseTimeRaw)
        );

        if (needsNormalization) {
            const normalizedCycles = callCycles.map((cycle: any) => ({
                ...cycle,
                countCalls: baseCount,
                timeBetweenCalls: baseCount === 1 ? 0 : baseTimeRaw
            }));

            dispatch(setCallCycles(normalizedCycles));
            dispatch(setChangedCompaniesData(true));
        }
    }, [companiesData?.campaign?.callsDialerType, callCycles]);

    useEffect(() => {
        if (resolveCallsDialerType(companiesData?.campaign) !== CALLS_DIALER_TYPE.sequentialByCycle || !Array.isArray(callCycles) || !callCycles.length) {
            return;
        }

        const baseTotalCount = callCycles[0]?.totalCount ?? null;
        const needsNormalization = callCycles.some((cycle: any) => cycle.totalCount !== baseTotalCount);

        if (needsNormalization) {
            const normalizedCycles = callCycles.map((cycle: any) => ({
                ...cycle,
                totalCount: baseTotalCount
            }));

            dispatch(setCallCycles(normalizedCycles));
            dispatch(setChangedCompaniesData(true));
        }
    }, [companiesData?.campaign?.callsDialerType, callCycles]);

    const updateState = (indexItem: number, name: string, value: any) => {
        const newState = callCycles?.map((obj: any) => {
            if (obj?.typeCall === indexItem) {
                if (name === 'countCalls' && value === 1) {
                    return {...obj, [name]: value, timeBetweenCalls: 0};
                }
                if (name === 'countCalls' && value === 0) {
                    return {...obj, [name]: 1, timeBetweenCalls: 0};
                } else {
                    return {...obj, [name]: value};
                }
            }
            return obj;
        });

        dispatch(setCallCycles(newState));
        dispatch(setErrorFields(null));
    };

    const setDefaultValue = (indexItem: number, name: string, value: any) => {
        const newState = callCycles?.map((obj: any) => {
            if (obj?.typeCall === indexItem && value === '') {
                return {...obj, [name]: 1, timeBetweenCalls: 0};
            }
            return obj;
        });
        dispatch(setCallCycles(newState));
        dispatch(setErrorFields(null))
    };

    const callCyclesErrors = errorFields?.callCycles;
    const hasCountCallsRangeError = !!callCyclesErrors && Object.values(callCyclesErrors).some((cycle: any) => {
        if (!cycle?.countCalls) {
            return false;
        }
        if (cycle.countCalls === true) {
            return true;
        }
        return typeof cycle.countCalls === 'string' && cycle.countCalls.includes('від 1 до 100');
    });
    const hasCountCallsExceedError = !!callCyclesErrors && Object.values(callCyclesErrors).some((cycle: any) =>
        typeof cycle?.countCalls === 'string' && cycle.countCalls.includes('перевищувати')
    );
    const hasCountCallsError = hasCountCallsRangeError || hasCountCallsExceedError;
    const hasTimeBetweenCallsError = !!callCyclesErrors && Object.values(callCyclesErrors).some((cycle: any) => !!cycle?.timeBetweenCalls);
    const hasCountCyclesError = !!errorFields?.countCycles;

    const callsDialerType = resolveCallsDialerType(companiesData?.campaign);
    const isSequentiallyByCycle = callsDialerType === CALLS_DIALER_TYPE.sequentialByCycle;
    const isCyclically = callsDialerType === CALLS_DIALER_TYPE.cyclically;
    const isSequentially = callsDialerType === CALLS_DIALER_TYPE.sequential;

    const globalCountValue = callCycles?.length ? (callCycles[0]?.countCalls ?? null) : null;
    const globalTimeBetweenValue = callCycles?.length ? (callCycles[0]?.timeBetweenCalls ?? null) : null;
    const globalCountCyclesValue = callCycles?.length ? (callCycles[0]?.totalCount ?? null) : null;

    const handleGlobalCountChange = (value: any) => {
        if (!isCyclically || !Array.isArray(callCycles) || !callCycles.length) {
            return;
        }

        const fallbackTime = globalCountValue === 1 ? null : globalTimeBetweenValue;
        const baseTimeFromCycle = callCycles[0]?.timeBetweenCalls;
        const effectiveFallbackTime = fallbackTime !== null && fallbackTime !== undefined
            ? fallbackTime
            : (baseTimeFromCycle && baseTimeFromCycle !== 0 ? baseTimeFromCycle : null);

        const updatedCycles = callCycles.map((cycle: any) => {
            if (value === null || value === undefined) {
                return {...cycle, countCalls: null, timeBetweenCalls: null};
            }

            const nextCount = value;
            const nextTime = nextCount === 1
                ? 0
                : effectiveFallbackTime;

            return {
                ...cycle,
                countCalls: nextCount,
                timeBetweenCalls: nextTime
            };
        });

        dispatch(setCallCycles(updatedCycles));
        dispatch(setChangedCompaniesData(true));
        dispatch(setErrorFields(null));
    };

    const handleGlobalTimeBetweenChange = (value: any) => {
        if (!isCyclically || !Array.isArray(callCycles) || !callCycles.length) {
            return;
        }

        const updatedCycles = callCycles.map((cycle: any) => ({
            ...cycle,
            timeBetweenCalls: value
        }));

        dispatch(setCallCycles(updatedCycles));
        dispatch(setChangedCompaniesData(true));
        dispatch(setErrorFields(null));
    };

    const isGlobalInputsDisabled = !archiveTableToggle || statusDone || !editType;
    const isGlobalTimeDisabled = isGlobalInputsDisabled || globalCountValue === 1 || globalCountValue === null;

    const handleDialOrderChange = (nextType: number) => {
        dispatch(setCompanyField({section: 'campaign', fieldName: 'callsDialerType', value: nextType}));

        if (nextType === CALLS_DIALER_TYPE.cyclically && Array.isArray(callCycles) && callCycles.length) {
            const baseCount = callCycles[0]?.countCalls ?? 1;
            const baseTime = baseCount === 1 ? 0 : (callCycles[0]?.timeBetweenCalls ?? 0);
            const normalizedCycles = callCycles.map((cycle: any) => ({
                ...cycle,
                countCalls: baseCount,
                timeBetweenCalls: baseCount === 1 ? 0 : baseTime
            }));
            dispatch(setCallCycles(normalizedCycles));
        }

        if (nextType === CALLS_DIALER_TYPE.sequentialByCycle && Array.isArray(callCycles) && callCycles.length) {
            const current = callCycles[0]?.totalCount ?? 1;
            const withTotalCount = callCycles.map((cycle: any) => ({
                ...cycle,
                totalCount: current
            }));
            dispatch(setCallCycles(withTotalCount));
        }

        dispatch(setChangedCompaniesData(true));
    };

    const handleCountCyclesChange = (value: any) => {
        if (!Array.isArray(callCycles) || !callCycles.length) {
            return;
        }

        const nextTotalCount = value === undefined ? null : value;
        const updatedCycles = callCycles.map((cycle: any) => ({
            ...cycle,
            totalCount: nextTotalCount
        }));

        dispatch(setCallCycles(updatedCycles));
        dispatch(setChangedCompaniesData(true));
        dispatch(setErrorFields(null));
    };

    const countCallsTemplate = (data: any, dataRow: any) => {
        const typeCall = data.typeCall || dataRow?.typeCall;
        let errorCountCalls = errorFields?.callCycles?.[typeCall]?.countCalls;
        const isDisabled =
            !archiveTableToggle ||
            statusDone ||
            !editType ||
            (!isCyclically && data.isActive === false);

        return (
            <div className="mt-3 relative">
                <div className="flex flex-column">
                    <InputNumber value={data.countCalls}
                                 style={{width: '80px'}}
                                 useGrouping={false}
                                 className={classNames('', {'p-invalid': errorCountCalls})}
                                 onChange={(e) => {
                                     updateState(typeCall, 'countCalls', e.value);
                                     dispatch(setChangedCompaniesData(true));
                                 }}
                                 onBlur={(e) => {
                                     setDefaultValue(typeCall, 'countCalls', e.target.value);
                                     dispatch(setChangedCompaniesData(true));
                                 }}
                                 disabled={isDisabled}
                    />
                </div>
            </div>
        )
    }

    const timeBetweenCallsTemplate = (data: any, dataRow: any) => {
        const typeCall = data.typeCall || dataRow?.typeCall;
        let errorTimeBetweenCalls = errorFields?.callCycles?.[typeCall]?.timeBetweenCalls;
        const isDisabled =
            !archiveTableToggle ||
            statusDone ||
            !editType ||
            data.countCalls === 1 ||
            (!isCyclically && data.isActive === false);
        return (
            <div className="mt-3 relative op">
                <div className="flex flex-column">
                    <InputNumber value={data.timeBetweenCalls}
                                 style={{width: '80px'}}
                                 useGrouping={false}
                                 className={classNames('', {'p-invalid': errorTimeBetweenCalls})}
                                 onChange={(e) => {
                                    updateState(typeCall, 'timeBetweenCalls', e.value);
                                    dispatch(setChangedCompaniesData(true));
                                 }}
                                 disabled={isDisabled}
                    />
                </div>
            </div>

        )
    }

    const isActiveTemplate = (data: any, dataRow: any) => {
        const typeCall = data.typeCall || dataRow?.typeCall;
        const isDisabled = !archiveTableToggle || statusDone || !editType;
        const isActive = data?.isActive !== false;

        return (
            <div className="mt-3 flex align-items-center justify-content-center">
                <Checkbox
                    inputId={`isActive-${typeCall}`}
                    className={classNames('', {'p-invalid': errorFields?.callCyclesActive})}
                    checked={isCyclically ? isActive : isActive}
                    disabled={isDisabled}
                    onChange={(e) => {
                        updateState(typeCall, 'isActive', e.checked);
                        dispatch(setChangedCompaniesData(true));
                    }}
                />
            </div>
        );
    }

    const resultCycleTemplate = (data: any, dataRow: any) => {
        const typeCall = data.typeCall || dataRow?.typeCall;
        const isDisabled =
            !archiveTableToggle ||
            statusDone ||
            !editType;
        const value = data?.isFullCycle ?? false;

        return (
            <div className="mt-3 flex align-items-center justify-content-center w-15rem">
                <InputSwitch
                    checked={value}
                    disabled={isDisabled}
                    onChange={(e) => {
                        updateState(typeCall, 'isFullCycle', e.value);
                        dispatch(setChangedCompaniesData(true));
                    }}
                />
            </div>
        );
    }

    const headerIsFullCycle = () => {
        return (
            <div className="flex flex-column w-15rem justify-content-center align-items-center">
                <div className="font-bold text-color-secondary">Успішний результат дзвінка</div>
                <div>Зупинити/Продовжити</div>
            </div>
        );
    }

    const activePhoneTypeIds = campaignDirectoriesPhoneType
        ?.filter((item: { isActive?: boolean }) => item.isActive !== false)
        ?.map((item: { dictionaryId: any }) => item.dictionaryId) || [];

    const filteredCallCycles = callCycles?.filter((cycle: any) =>
        activePhoneTypeIds.includes(cycle.typeCall)
    ) || [];

    const typeTemplate = (data: any) => {
        let name = campaignDirectoriesPhoneType.find((item: { dictionaryId: any; isActive?: boolean; }) => item.dictionaryId === data.typeCall && item.isActive !== false);
        return (
            <div className='mt-3 relative' style={{maxWidth: '250px'}}>
                {campaignDirectoriesPhoneType && campaignDirectoriesPhoneType.length && name && <InputText value={name?.name}
                                                                                                   style={{opacity: '1', minWidth: '250px', maxWidth: '250px'}}
                                                                                                   className={classNames('w-full', {'opacity-40': statusDone || !editType})}
                                                                                                   disabled
                />}
            </div>
        )
    }

    const onRowReorder = (e: {
        dragIndex: number;
        dropIndex: number;
        value: any[];
    }) => {
        const reordered = (e.value || []).map((row: any, index: number) => ({
            ...row,
            priorityCallType: index + 1
        }));
        dispatch(setCallCycles(reordered));
    };

    const getCallCyclesErrorMessage = () => {
        const messages = [];
        if (hasCountCallsRangeError) {
            messages.push('Значення кількості спроб повинно бути від 1 до 100');
        }
        if (hasCountCallsExceedError) {
            messages.push('Кількість спроб не може перевищувати кількість циклів');
        }
        if (hasTimeBetweenCallsError) {
            messages.push('значення час між спробами повинно бути від 1 до 1000');
        }
        if (errorFields?.countCycles?.countCycles) {
            messages.push('Значення кількості циклів повинно бути від 1 до 100');
        }
        if (errorFields?.countCycles?.sequentialByCycleMatch) {
            messages.push(errorFields.countCycles.sequentialByCycleMatch.replace('*', ''));
        }
        if (errorFields?.callCyclesActive?.message) {
            messages.push(errorFields.callCyclesActive.message.replace('*', ''));
        }

        if (messages.length === 0) {
            return null;
        }

        return messages.join(', ') + '*';
    };

    const callCyclesError = getCallCyclesErrorMessage();

    return (
        <div className="ring-cycle">
            <div className="grid p-fluid">
                <div className="col-7 md:col-7">
                    <h6 className="mr-2 mb-0">Порядок продзвону:</h6>
                    <div className="flex flex-wrap align-items-center my-4">
                        <div className="field-radiobutton mr-4 mb-0">
                            <RadioButton
                                inputId="dialOrderSequential"
                                name="dialOrder"
                                value={CALLS_DIALER_TYPE.sequential}
                                checked={isSequentially}
                                disabled={isGlobalInputsDisabled}
                                onChange={() => handleDialOrderChange(CALLS_DIALER_TYPE.sequential)}
                            />
                            <label htmlFor="dialOrderSequential" className="ml-2">Послідовно</label>
                        </div>
                        <div className="field-radiobutton mr-4 mb-0">
                            <RadioButton
                                inputId="dialOrderCyclically"
                                name="dialOrder"
                                value={CALLS_DIALER_TYPE.cyclically}
                                checked={isCyclically}
                                disabled={isGlobalInputsDisabled}
                                onChange={() => handleDialOrderChange(CALLS_DIALER_TYPE.cyclically)}
                            />
                            <label htmlFor="dialOrderCyclically" className="ml-2">Циклічно</label>
                        </div>
                        <div className="field-radiobutton mb-0">
                            <RadioButton
                                inputId="dialOrderSequentialByCycle"
                                name="dialOrder"
                                value={CALLS_DIALER_TYPE.sequentialByCycle}
                                checked={isSequentiallyByCycle}
                                disabled={isGlobalInputsDisabled}
                                onChange={() => handleDialOrderChange(CALLS_DIALER_TYPE.sequentialByCycle)}
                            />
                            <label htmlFor="dialOrderSequentialByCycle" className="ml-2">Послідовно по циклу</label>
                        </div>
                    </div>
                </div>
                {/*<div className="col-4 md:col-4"></div>*/}
                <div className="flex-column field col-4 md:col-4 mb-0 pl-2 ml-3">
                    <div>
                        {companiesData?.campaign?.campaignTypeId === 204 ?<div className={classNames('mb-3 font-semibold', {'p-error': errorFields?.ivmListeningTime})}>Тривалість прослуховування (мін. 1 сек)*</div>:
                            <div className={classNames('mb-3 font-semibold ', {'p-error': errorFields?.ivmListeningTime})}>Тривалість розмови (мін. 1 сек)*</div>}
                    </div>
                    <InputNumber
                        min={0}
                        id="maxWaitTime"
                        value={companiesData?.campaign?.doneDurationTime || null}
                        useGrouping={false}
                        className={classNames('max-w-20rem', {'p-invalid': errorFields?.ivmListeningTime})}
                        disabled={!archiveTableToggle || !editType}
                        onChange={(e) => {
                            dispatch(setCompanyField({section: 'campaign', fieldName: 'doneDurationTime', value: e.value}));
                        }}
                        placeholder="0"
                    />
                    {errorFields?.ivmListeningTime && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span
                        className="title p-error text-sm">{errorFields?.ivmListeningTime?.ivmListeningTime}</span></div>}
                </div>
                <Divider/>
                <div className="field col-12 md:col-8 p-0 m-0">
                    {isSequentially && <div className="grid p-fluid">
                        <div className="grid p-fluid w-full">
                            <div className="col-12 md:col-12">
                                <DataTable
                                    value={[...filteredCallCycles].sort((a, b) => a.priorityCallType - b.priorityCallType)}
                                    emptyMessage={<></>}
                                    reorderableColumns
                                    reorderableRows
                                    rowClassName={(rowData) => {
                                        const invalidTrunks = errorFields?.trankId?.trunkIds;
                                        return Array.isArray(invalidTrunks) && invalidTrunks.includes(rowData.trunkId)
                                            ? 'p-invalid'
                                            : '';
                                    }}
                                    onRowReorder={onRowReorder}
                                    className="">
                                    <Column rowReorder style={{width: '3rem'}} className="pt-4 desktop"/>
                                    <Column key="type" bodyStyle={{maxWidth: '250px'}} headerStyle={{maxWidth: '250px'}} style={{maxWidth: '250px'}} columnKey="type" field="type" header="Тип контакту ліда" body={typeTemplate}/>
                                    <Column key="isActive" columnKey="isActive" field="isActive" header="Активний" body={isActiveTemplate} headerClassName="pl-4" style={{width: '8rem'}}/>
                                    <Column key="resultCycle" columnKey="resultCycle" header={headerIsFullCycle} headerStyle={{maxWidth: '250px'}} style={{maxWidth: '250px', width: '200px'}} field="resultCycle" body={resultCycleTemplate}/>
                                    <Column key="countCalls" columnKey="countCalls" field="countCalls" header="Кількість спроб" body={countCallsTemplate}/>
                                    <Column key="timeBetweenCalls" columnKey="timeBetweenCalls" field="timeBetweenCalls" header="Час між спробами хв." body={timeBetweenCallsTemplate}/>
                                </DataTable>
                            </div>
                        </div>
                        <div className="col-12 md:col-12">
                            {callCyclesError && (
                                <div className="title p-error text-sm">
                                    {callCyclesError}*
                                </div>
                            )}
                        </div>
                    </div>}
                    {isCyclically && <div className="grid p-fluid">
                        <div className="grid p-fluid">
                            <div className="col-12 md:col-12 flex">
                                <DataTable
                                    value={[...filteredCallCycles].sort((a, b) => a.priorityCallType - b.priorityCallType)}
                                    emptyMessage={<></>}
                                    reorderableColumns
                                    reorderableRows
                                    rowClassName={(rowData) => {
                                        const invalidTrunks = errorFields?.trankId?.trunkIds;
                                        return Array.isArray(invalidTrunks) && invalidTrunks.includes(rowData.trunkId)
                                            ? 'p-invalid'
                                            : '';
                                    }}
                                    onRowReorder={onRowReorder}
                                    className="w-full">
                                    <Column rowReorder style={{width: '3rem', marginRight: '200px'}} bodyClassName={'pr-4 desktop'} className="pt-4"/>
                                    <Column key="type" columnKey="type" field="type" header="Тип контакту ліда" body={typeTemplate}/>
                                    <Column key="isActive" columnKey="isActive" field="isActive" header="Активний" body={isActiveTemplate} style={{width: '8rem'}}/>
                                    <Column key="resultCycle" columnKey="resultCycle" field="resultCycle" header={headerIsFullCycle} body={resultCycleTemplate}/>
                                </DataTable>
                                <div className="pt-1">
                                    <Divider className="ml-4 mr-3 my-5" layout="vertical"/>
                                </div>
                                <div className="col-4 md:col-4 pt-1" style={{width: '60%'}}>
                                    <div className="col-12 md:col-12 flex w-full flex-wrap pt-0 ">
                                        <div className="col-12 md:col-4 p-column-header-content pt-3 pb-3" style={{border: '1px solid rgba(255, 255, 255, 0.15)',
                                            borderWidth: '0 0 1px 0',}}><span style={{textAlign: 'left',
                                            padding: '0.571rem 0.571rem',
                                            color: '#868C9B',
                                        }} className="p-column-title">Кількість спроб</span></div>
                                        <div className="col-12 md:col-5 p-column-header-content pt-3 pb-3" style={{border: '1px solid rgba(255, 255, 255, 0.15)',
                                            borderWidth: '0 0 1px 0',}}><span style={{textAlign: 'left',
                                            padding: '0.571rem 0.571rem',
                                            color: '#868C9B',
                                        }} className="p-column-title mb-3">Час між спробами (хв.)</span></div>
                                    </div>
                                    <div className="col-12 md:col-12 flex w-full flex-wrap">
                                        <div className="col-12 md:col-4 p-column-header-content">
                                            <div className="flex flex-column">
                                                <InputNumber
                                                    value={globalCountValue}
                                                    style={{width: '120px'}}
                                                    useGrouping={false}
                                                    className={classNames('max-w-10rem', {'p-invalid': hasCountCallsError})}
                                                    onChange={(e) => {
                                                        handleGlobalCountChange(e.value);
                                                    }}
                                                    disabled={isGlobalInputsDisabled}
                                                />
                                                {hasCountCallsError && (
                                                    <small className="p-error mt-1">
                                                        Значення кількості спроб повинно бути від 1 до 100
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-4 p-column-header-content">
                                            <div className="flex flex-column">
                                                <InputNumber
                                                    value={globalCountValue === 1 ? 0 : globalTimeBetweenValue}
                                                    style={{width: '120px'}}
                                                    useGrouping={false}
                                                    className={classNames('max-w-10rem', {'p-invalid': hasTimeBetweenCallsError})}
                                                    onChange={(e) => {
                                                        handleGlobalTimeBetweenChange(e.value);
                                                    }}
                                                    disabled={isGlobalTimeDisabled}
                                                />
                                                {hasTimeBetweenCallsError && (
                                                    <small className="p-error mt-1">
                                                        значення час між спробами повинно бути від 1 до 1000
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 md:col-12">
                            {errorFields?.trankId?.campaignTranks && (
                                <div className="title p-error text-sm">
                                    {errorFields.trankId.campaignTranks}*
                                </div>
                            )}
                            {callCyclesError && (
                                <div className="title p-error text-sm">
                                    {callCyclesError}*
                                </div>
                            )}
                        </div>
                    </div>}
                </div>
                {isSequentiallyByCycle && <div className="field col-12 md:col-12">
                    <div className="grid p-fluid">
                        <div className="grid p-fluid">
                            <div className="col-12 md:col-12 flex">
                                <DataTable
                                    value={[...filteredCallCycles].sort((a, b) => a.priorityCallType - b.priorityCallType)}
                                    emptyMessage={<></>}
                                    reorderableColumns
                                    reorderableRows
                                    rowClassName={(rowData) => {
                                        const invalidTrunks = errorFields?.trankId?.trunkIds;
                                        return Array.isArray(invalidTrunks) && invalidTrunks.includes(rowData.trunkId)
                                            ? 'p-invalid'
                                            : '';
                                    }}
                                    onRowReorder={onRowReorder}
                                    className="w-full">
                                    <Column rowReorder style={{width: '3rem'}} className="pt-4 desktop"/>
                                    <Column key="type" bodyStyle={{maxWidth: '250px'}} headerStyle={{maxWidth: '250px'}} style={{maxWidth: '250px'}} columnKey="type" field="type" header="Тип контакту ліда" body={typeTemplate}/>
                                    <Column key="isActive" columnKey="isActive" field="isActive" header="Активний" body={isActiveTemplate} headerClassName="pl-4" style={{width: '8rem'}}/>
                                    <Column key="resultCycle" columnKey="resultCycle" header={headerIsFullCycle} headerStyle={{maxWidth: '250px'}} style={{maxWidth: '250px', width: '200px'}} field="resultCycle" body={resultCycleTemplate}/>
                                    <Column key="countCalls" columnKey="countCalls" field="countCalls" header="Кількість спроб" body={countCallsTemplate}/>
                                </DataTable>
                                <div className="pt-1">
                                    <Divider className="ml-4 mr-3 my-5" layout="vertical"/>
                                </div>
                                <div className="col-4 md:col-4 pt-1">
                                    <div className="col-12 md:col-12 flex w-full flex-wrap pt-0 ">
                                        <div className="col-12 md:col-12 p-column-header-content pt-3 pb-3" style={{border: '1px solid rgba(255, 255, 255, 0.15)',
                                            borderWidth: '0 0 1px 0',}}><span style={{textAlign: 'left',
                                            padding: '0.571rem 0.571rem',
                                            color: '#868C9B',
                                        }} className="p-column-title">Кількість циклів</span></div>
                                    </div>
                                    <div className="col-12 md:col-12 flex w-full flex-wrap">
                                        <div className="col-12 md:col-12 p-column-header-content">
                                            <div className="flex flex-column">
                                                <InputNumber
                                                    value={globalCountCyclesValue}
                                                    style={{width: '120px'}}
                                                    useGrouping={false}
                                                    className={classNames('max-w-10rem', {'p-invalid': hasCountCyclesError})}
                                                    onChange={(e) => {
                                                        handleCountCyclesChange(e.value);
                                                    }}
                                                    disabled={isGlobalInputsDisabled}
                                                />
                                                {errorFields?.countCycles?.countCycles && (
                                                    <small className="p-error mt-1">
                                                        Значення кількості циклів повинно бути від 1 до 100
                                                    </small>
                                                )}
                                                {errorFields?.countCycles?.sequentialByCycleMatch && (
                                                    <small className="p-error mt-1">
                                                        {errorFields.countCycles.sequentialByCycleMatch}
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 md:col-12">
                            {callCyclesError && (
                                <div className="title p-error text-sm">
                                    {callCyclesError}*
                                </div>
                            )}
                        </div>
                    </div>
                </div>}
            </div>
        </div>
    )
}

export default QueueACDSetting
